import type { FC } from "react";
import {
    AlertCircle,
    Announcement02,
    Award03,
    BarChart10,
    Bell01,
    CheckCircle,
    CoinsStacked01,
    Cursor03,
    Edit05,
    FlipBackward,
    Gift01,
    HeartHand,
    LayersThree01,
    MessageSmileSquare,
    Receipt,
    ReverseLeft,
    Rocket02,
    ShoppingBag03,
    TrendDown01,
    TrendUp01,
    Wallet02,
    Ticket02,
    XCircle,
} from "@untitledui/icons";
import { Button as AriaButton } from "react-aria-components";
import { IconChip, JalaliDate, cx, LatinRuns } from "@mfa-aff/ui";
import { SlideoutMenu } from "@mfa-aff/ui/components/application/slideout-menus/slideout-menu";
import { Button } from "@mfa-aff/ui/components/base/buttons/button";
import { Link, useLocation } from "react-router";
import type { PartnerNotificationView } from "@mfa-aff/shared";
import { useMarkAllNotificationsRead, useMarkNotificationRead, useNotifications } from "../api/me";
import { FEEDBACK_HASH } from "./FeedbackDialog";

type Kind = { icon: FC<{ className?: string }>; color: "brand" | "success" | "warning" | "error" | "gray"; href?: string };

/** Icon, tone and panel route per stored notification `type`; unknown types fall back by prefix. */
const NOTIFICATION_KIND: Record<string, Kind> = {
    "commission.approved": { icon: CheckCircle, color: "success", href: "/orders" },
    "commission.reversed": { icon: ReverseLeft, color: "error", href: "/orders" },
    "payout.settled": { icon: Wallet02, color: "success", href: "/wallet" },
    "payout.failed": { icon: AlertCircle, color: "error", href: "/wallet" },
    "payout.reversed": { icon: ReverseLeft, color: "brand", href: "/wallet" },
    "level.grace": { icon: TrendDown01, color: "warning", href: "/levels" },
    "level.demoted": { icon: TrendDown01, color: "error", href: "/levels" },
    "bonus.first_sale": { icon: Gift01, color: "success", href: "/wallet" },
    "level.upgraded": { icon: TrendUp01, color: "success", href: "/levels" },
    // A contract partner moved to the level ladder («تغییر قرارداد»).
    "contract.switched": { icon: LayersThree01, color: "brand", href: "/levels" },
    "lifecycle.welcome": { icon: HeartHand, color: "brand", href: "/refcode" },
    "promo.batch_low": { icon: Ticket02, color: "warning", href: "/codes?type=limited" },
    "promo.batch_full": { icon: Ticket02, color: "warning", href: "/codes?type=limited" },
    "promo.pack_low": { icon: Ticket02, color: "warning", href: "/codes?type=pack" },
    "promo.pack_decided": { icon: Ticket02, color: "brand", href: "/codes?type=pack" },
    "lifecycle.first_click": { icon: Cursor03, color: "success", href: "/links/stats" },
    "lifecycle.first_order": { icon: ShoppingBag03, color: "success", href: "/orders" },
    "lifecycle.monthly_summary": { icon: BarChart10, color: "brand", href: "/earnings" },
    "lifecycle.inactive": { icon: Announcement02, color: "warning", href: "/kit" },
    "campaign.message": { icon: Announcement02, color: "brand" },
    "sales_campaign.started": { icon: Rocket02, color: "brand", href: "/special/campaign" },
    "sales_campaign.achieved": { icon: Award03, color: "success", href: "/special/campaign" },
    "sales_campaign.ended": { icon: XCircle, color: "gray", href: "/special/campaign" },
    "sales_campaign.settled": { icon: Wallet02, color: "success", href: "/special/campaign" },
    "turnover.joined": { icon: CoinsStacked01, color: "brand", href: "/special/turnover" },
    "turnover.left": { icon: FlipBackward, color: "gray", href: "/special/turnover" },
    "turnover.settled": { icon: Wallet02, color: "success", href: "/special/turnover" },
    // Opens «پیشنهاد / گزارش مشکل» over the current page, where the reply is listed.
    "feedback.replied": { icon: MessageSmileSquare, color: "brand", href: FEEDBACK_HASH },
};
const PREFIX_KIND: Record<string, Kind> = {
    commission: { icon: Receipt, color: "brand", href: "/orders" },
    claim: { icon: Receipt, color: "brand", href: "/orders?tab=claims" },
    payout: { icon: Wallet02, color: "brand", href: "/wallet" },
    level: { icon: Award03, color: "brand", href: "/levels" },
    rules: { icon: Edit05, color: "warning", href: "/rules" },
    rates: { icon: Edit05, color: "warning", href: "/rules" },
    sales_campaign: { icon: Rocket02, color: "brand", href: "/special/campaign" },
    turnover: { icon: CoinsStacked01, color: "brand", href: "/special/turnover" },
};

export const notificationKind = (type: string): Kind =>
    NOTIFICATION_KIND[type] ?? PREFIX_KIND[type.split(".")[0] ?? ""] ?? { icon: Bell01, color: "gray" };

/** One inbox row; shared by the bell slideout and the notifications page. */
export const NotificationRow = ({ n, onOpen }: { n: PartnerNotificationView; onOpen?: () => void }) => {
    const kind = notificationKind(n.type);
    const { pathname, search } = useLocation();
    const read = n.readAt !== null;
    // A bare `#…` target (opens a dialog over the current page) keeps the page's own query, e.g. a tab.
    const to = kind.href?.startsWith("#") ? { pathname, search, hash: kind.href } : kind.href;
    const body = (
        <div className={cx("flex gap-3 rounded-xl p-3 transition duration-100 ease-linear hover:bg-primary_hover", !read && "bg-brand-primary_alt/60 dark:bg-brand-500/10")}>
            <IconChip icon={kind.icon} color={kind.color} />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-primary">
                        <LatinRuns text={n.title} />
                    </p>
                    {!read && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-fg-brand-primary" aria-label="خوانده نشده" />}
                </div>
                <p className="text-sm whitespace-pre-line text-tertiary">
                    <LatinRuns text={n.body} />
                </p>
                <JalaliDate value={n.createdAt} format="datetime" className="text-xs text-quaternary" />
            </div>
        </div>
    );
    return to ? (
        <Link to={to} onClick={onOpen} className="block rounded-xl outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2">
            {body}
        </Link>
    ) : (
        body
    );
};

/** Header bell with unread count; opens the inbox as a slideout (spec §14 «صندوق اعلان»). */
export const NotificationBell = () => {
    const { data: pages } = useNotifications();
    const markAll = useMarkAllNotificationsRead();
    const markRead = useMarkNotificationRead();
    const data = pages?.pages.flatMap((p) => p.items);
    const unread = pages?.pages[0]?.unread ?? 0;

    return (
        <SlideoutMenu.Trigger>
            <AriaButton
                aria-label={unread ? `اعلان‌ها، ${unread} خوانده‌نشده` : "اعلان‌ها"}
                className="relative flex size-10 cursor-pointer items-center justify-center rounded-lg text-fg-secondary outline-focus-ring hover:bg-primary_hover hover:text-fg-secondary_hover focus-visible:outline-2 focus-visible:outline-offset-2"
            >
                <Bell01 className="size-5" aria-hidden="true" />
                {unread > 0 && (
                    <span className="absolute top-1.5 end-1.5 flex min-w-4 items-center justify-center rounded-full bg-error-solid px-1 text-[10px] leading-4 font-semibold text-white digits-tabular">
                        {unread}
                    </span>
                )}
            </AriaButton>
            <SlideoutMenu isDismissable>
                {({ close }) => (
                    <>
                        <SlideoutMenu.Header onClose={close} className="flex flex-col gap-1 pe-12">
                            <h2 className="text-lg font-semibold text-primary">اعلان‌ها</h2>
                            <p className="text-sm text-tertiary">پورسانت‌ها، تسویه‌ها و تغییر نرخ‌ها؛ همین پیام‌ها پیامک هم می‌شوند.</p>
                        </SlideoutMenu.Header>
                        <SlideoutMenu.Content className="gap-1">
                            {data?.length === 0 && (
                                <p className="py-10 text-center text-sm text-tertiary">هنوز اعلانی ندارید. با اولین پورسانت، اینجا خبرتان می‌کنیم.</p>
                            )}
                            {data?.slice(0, 8).map((n) => (
                                <NotificationRow
                                    key={n.id}
                                    n={n}
                                    onOpen={() => {
                                        if (n.readAt === null) markRead.mutate(n.id);
                                        close();
                                    }}
                                />
                            ))}
                        </SlideoutMenu.Content>
                        <SlideoutMenu.Footer className="flex items-center justify-between gap-3">
                            <Button color="link-gray" size="sm" isDisabled={!unread} onPress={() => markAll.mutate()}>
                                همه خوانده شد
                            </Button>
                            <Button color="secondary" size="sm" href="/notifications" onPress={close}>
                                همهٔ اعلان‌ها
                            </Button>
                        </SlideoutMenu.Footer>
                    </>
                )}
            </SlideoutMenu>
        </SlideoutMenu.Trigger>
    );
};
